import { useEffect, useRef, useState } from 'react';
import { heardBetween, isNewPlayback } from './playback.js';

/**
 * The recording player.
 *
 * Deliberately built on `<input type="range">` rather than a custom-drawn scrubber. A div with
 * pointer handlers has to reimplement keyboard support, focus, and value announcement, and
 * usually reimplements them badly. The native control arrives with arrow keys, Home/End,
 * Page Up/Down and a screen-reader value already correct.
 *
 * **The waveform is real data or it is not drawn.** `peaks` comes from the API, extracted from
 * the recording itself during sync. When there are none - a text-only call, or a provider stub
 * with no audio in it - the scrubber stays a plain bar. A shape invented to look like amplitude
 * would fail the honesty directive and THE-BAR at once, which is why the original "decorative
 * waveform" recommendation was withdrawn rather than implemented.
 *
 * The bars are decoration ON TOP of the native input, never a replacement for it:
 * `aria-hidden`, pointer-events off, and every interaction still goes through the range.
 */
export function Player({ src, durationSeconds, title, peaks, onPlaybackStart, onListened }: {
  src: string; durationSeconds: number; title: string; peaks?: readonly number[] | null;
  /** Called once per playback - see isNewPlayback. The page logs it; the player only reports it. */
  onPlaybackStart?: () => void;
  /** Seconds actually heard since the last call - on pause, at the end, and on leaving. */
  onListened?: (seconds: number) => void;
}) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const playback = useRef({ inProgress: false, furthest: 0 });
  // Listening time not yet reported, and where the head was at the last update.
  const heard = useRef({ pending: 0, last: 0 });
  const listened = useRef(onListened);
  useEffect(() => { listened.current = onListened; }, [onListened]);
  const flush = () => {
    const s = heard.current.pending;
    heard.current.pending = 0;
    if (s >= 1) listened.current?.(s);
  };

  // Leaving the page or the call reports what was heard, so a closed tab is not lost time.
  useEffect(() => {
    const onHide = () => { if (document.visibilityState === 'hidden') flush(); };
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', flush);
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', flush);
      flush();
    };
  }, [src]);

  useEffect(() => {
    setPosition(0); setPlaying(false);
    playback.current = { inProgress: false, furthest: 0 };
  }, [src]);

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) { void el.play(); setPlaying(true); } else { el.pause(); setPlaying(false); }
  };

  const seek = (to: number) => {
    const el = audioRef.current;
    if (el) el.currentTime = to;
    setPosition(to);
  };

  const total = durationSeconds || 0;
  // Both null and an empty array mean "no waveform" - empty is the API recording that it tried
  // and there was nothing to extract.
  const hasPeaks = peaks != null && peaks.length > 0;

  return (
    <div className="player">
      <audio
        ref={audioRef}
        src={src}
        preload="auto"
        onTimeUpdate={(e) => {
          const at = e.currentTarget.currentTime;
          setPosition(at);
          if (at > playback.current.furthest) playback.current.furthest = at;
          if (!e.currentTarget.paused) heard.current.pending += heardBetween(heard.current.last, at);
          heard.current.last = at;
        }}
        onSeeked={(e) => { heard.current.last = e.currentTarget.currentTime; }}
        onEnded={() => { setPlaying(false); playback.current.inProgress = false; flush(); }}
        onPause={() => { setPlaying(false); flush(); }}
        onPlay={(e) => {
          setPlaying(true);
          const at = e.currentTarget.currentTime;
          heard.current.last = at;
          if (isNewPlayback(playback.current, at)) {
            playback.current = { inProgress: true, furthest: at };
            onPlaybackStart?.();
          }
        }}
      />
      <button
        className="btn player-toggle"
        onClick={toggle}
        aria-label={playing ? `Pause ${title}` : `Play ${title}`}
      >
        {playing ? 'Pause' : 'Play'}
      </button>

      <div className="scrub-wrap">
        {hasPeaks && (
          <div className="wave" aria-hidden="true">
            {peaks!.map((p, i) => (
              <span
                key={i}
                className={`wave-bar${(i / peaks!.length) * total <= position ? ' wave-played' : ''}`}
                // 3% floor so a silent moment is still a visible mark rather than a gap the
                // eye reads as missing data.
                style={{ height: `${3 + (p / 1000) * 97}%` }}
              />
            ))}
          </div>
        )}
      <input
        className={hasPeaks ? 'scrubber scrubber-over-wave' : 'scrubber'}
        type="range"
        min={0}
        max={Math.max(total, 1)}
        step={1}
        value={Math.min(position, total)}
        onChange={(e) => seek(Number(e.target.value))}
        aria-label="Position in the recording"
        // The printed value is "3:07"; a screen reader must not read that as "three oh seven".
        aria-valuetext={`${spoken(position)} of ${spoken(total)}`}
      />
      </div>

      <span className="player-time" aria-hidden="true">
        {clock(position)} / {clock(total)}
      </span>
    </div>
  );
}

export const clock = (s: number) =>
  `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

/** The spoken form of a duration. "3:07" is not a number a screen reader can read usefully. */
export function spoken(seconds: number): string {
  const s = Math.floor(seconds);
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m === 0) return `${r} second${r === 1 ? '' : 's'}`;
  if (r === 0) return `${m} minute${m === 1 ? '' : 's'}`;
  return `${m} minute${m === 1 ? '' : 's'} ${r} second${r === 1 ? '' : 's'}`;
}
